import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  useTheme
} from '@mui/material';
import { Question } from '../../types';
import { AudioRecorderCore } from './AudioRecorderCore';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

interface Props {
  question: Question;
  textValue: string;
  audioBlob: Blob | null;
  onTextChange: (value: string) => void;
  onAudioChange: (blob: Blob | null, transcription?: string) => void;
  isSubmitting?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`response-tabpanel-${index}`}
      aria-labelledby={`response-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, backgroundColor: 'transparent' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `response-tab-${index}`,
    'aria-controls': `response-tabpanel-${index}`,
  };
}

export const TextOrAudioResponseQuestion: React.FC<Props> = ({
  question,
  textValue,
  audioBlob,
  onTextChange,
  onAudioChange,
  isSubmitting = false,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsiveLayout();
  const [activeTab, setActiveTab] = useState(audioBlob ? 1 : 0);
  const [hasTranscribedAudio, setHasTranscribedAudio] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Switch to audio tab if there's an audio blob
    if (audioBlob && activeTab === 0) {
      setActiveTab(1);
    }
  }, [audioBlob]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('[TextOrAudioResponseQuestion] Tab change:', {
      newValue,
      hasAudioBlob: !!audioBlob,
      hasTranscribedAudio,
      currentText: textValue
    });

    if (isSubmitting) return;
    
    if (newValue === 0 && audioBlob && !hasTranscribedAudio) {
      if (!window.confirm('Switching to text will clear your voice recording. Continue?')) {
        return;
      }
    }
    
    setActiveTab(newValue);
    if (newValue === 0 && !hasTranscribedAudio) {
      console.log('[TextOrAudioResponseQuestion] Clearing audio on tab switch');
      onAudioChange(null);
    } else if (newValue === 1) {
      console.log('[TextOrAudioResponseQuestion] Switching to Voice tab, text preserved');
      // onTextChange(''); // Text is preserved
      setHasTranscribedAudio(false);
    }
  };

  const handleRecordingComplete = (blob: Blob | null, transcription?: string) => {
    console.log('[TextOrAudioResponseQuestion] Recording complete:', {
      hasBlob: !!blob,
      blobSize: blob?.size,
      hasTranscription: !!transcription,
      transcriptionLength: transcription?.length
    });

    if (blob === null) {
      const wasTranscribed = hasTranscribedAudio;
      onAudioChange(null);
      setHasTranscribedAudio(false); // Always reset this as audio is gone/cleared

      if (wasTranscribed) {
        console.log('[TextOrAudioResponseQuestion] Audio cleared, also clearing its transcription from text field.');
        onTextChange('');
      } else {
        console.log('[TextOrAudioResponseQuestion] Audio cleared, preserving manually entered/edited text.');
        // No change to textValue, text is preserved
      }
    } else {
      console.log('[TextOrAudioResponseQuestion] Setting audio and text');
      onAudioChange(blob, transcription);
      if (transcription) {
        onTextChange(transcription);
        setHasTranscribedAudio(true);
      }
    }
  };

  const handleTranscriptionStart = () => {
    console.log('[TextOrAudioResponseQuestion] Transcription started');
    setError(null);
  };

  const handleError = (errorMessage: string) => {
    console.error('[TextOrAudioResponseQuestion] Error:', errorMessage);
    setError(errorMessage);
    onAudioChange(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="response type tabs">
          <Tab label="Text" {...a11yProps(0)} />
          <Tab label="Voice" {...a11yProps(1)} />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TextField
            multiline
            rows={4}
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={isSubmitting}
            placeholder="Type your response here..."
            sx={{
              width: '100%',
              minWidth: isMobile ? '100%' : '600px',
              maxWidth: isMobile ? '100%' : '800px',
              backgroundColor: 'rgba(38, 38, 38, 0.6)',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputBase-input': {
                color: 'text.primary',
                fontSize: isMobile ? '16px' : 'inherit', // Prevent zoom on mobile devices
              },
            }}
          />
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ 
          width: '100%', 
          minWidth: isMobile ? '100%' : '600px',
          maxWidth: isMobile ? '100%' : '800px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: isMobile ? 0 : 2
        }}>
          <AudioRecorderCore
            onRecordingComplete={handleRecordingComplete}
            onTranscriptionStart={handleTranscriptionStart}
            onError={handleError}
            isSubmitting={isSubmitting}
          />
          
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2, fontSize: isMobile ? '14px' : '16px' }}>
              {error}
            </Typography>
          )}
        </Box>
      </TabPanel>
    </Box>
  );
};
